import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { signOut, updateProfile, deleteUser, User } from "firebase/auth";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { doc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { auth, storage, db } from "../firebase";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { fx_FB_DeleteUserAccount } from "../functions/Fx_FirebaseUpdateFunctions";

export interface UseProfileActionsProps {
  profile: {
    UserId: string;
    FirstName: string;
  };
  currentUser: User;
  presentAlert: (options: {
    header: string;
    message?: string;
    buttons: Array<string | { text: string; handler?: () => void }>;
  }) => void;
  isDesktopMode?: boolean;
  onEditProfileRequest?: () => void;
}

/**
 * Hook for managing profile-related actions
 * Used across mobile and desktop profile pages for consistent behavior
 * 
 * Provides handlers for:
 * - Profile photo upload
 * - User logout
 * - Account deletion
 * - Admin permission checks
 * - Profile navigation
 */
export const useProfileActions = ({
  profile,
  currentUser,
  presentAlert,
  isDesktopMode = false,
  onEditProfileRequest,
}: UseProfileActionsProps) => {
  const history = useHistory();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin permissions
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const q = query(
          collection(db, "userSettings"),
          where("userID", "==", "admin")
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const exists = data.adminUIDs.includes(profile.UserId);
          setIsAdmin(exists);
        });
      } catch (err) {
        console.error("Error fetching admin settings:", err);
      }
    };

    if (profile.UserId) {
      fetchAdminSettings();
    }
  }, [profile.UserId]);

  /**
   * Upload and update profile photo across all Firestore collections
   * Includes state guard to prevent double-clicks
   */
  const uploadProfilePhoto = useCallback(async () => {
    if (uploadingPhoto) return;

    try {
      setUploadingPhoto(true);

      const result = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        source: CameraSource.Photos,
        resultType: CameraResultType.Base64,
      });

      if (!result) {
        // console.log("Profile photo: no result returned from Camera.getPhoto");
        setUploadingPhoto(false);
        return;
      }

      if (!result.base64String) {
        // console.log("Profile photo: no base64String returned from Camera.getPhoto");
        presentAlert({
          header: "Photo Upload Failed",
          message: "No image data was returned. Please try again.",
          buttons: ["OK"],
        });
        setUploadingPhoto(false);
        return;
      }

      const imageFormat = result.format || "jpeg";
      const date = new Date().getTime();
      const storageRef = ref(
        storage,
        `${profile.UserId}/profilePic/${date}.${imageFormat}`
      );

      await uploadString(storageRef, result.base64String, "base64");
      const downloadURL = await getDownloadURL(storageRef);

      if (!currentUser) {
        // console.log("Profile photo: currentUser was null after upload");
        presentAlert({
          header: "Photo Upload Failed",
          message: "User session was unavailable. Please log in again and retry.",
          buttons: ["OK"],
        });
        setUploadingPhoto(false);
        return;
      }

      // Update auth profile
      await updateProfile(currentUser, {
        photoURL: downloadURL,
      });

      // Update all Firestore collections
      const userDocRef = doc(db, FB_COLLECTION_PATH.Users, profile.UserId);
      const userDocRef2 = doc(
        db,
        `${FB_COLLECTION_PATH.Users}/${profile.UserId}/users_AssociateFacing`,
        profile.UserId
      );
      const userDocRef3 = doc(
        db,
        `${FB_COLLECTION_PATH.Users}/${profile.UserId}/${FB_COLLECTION_PATH.UsersPublicFacing}`,
        profile.UserId
      );

      await Promise.all([
        updateDoc(userDocRef, { profilePicURL: downloadURL }),
        updateDoc(userDocRef2, { profilePicURL: downloadURL }),
        updateDoc(userDocRef3, { profilePicURL: downloadURL }),
        updateProfile(currentUser, { photoURL: downloadURL }),
      ]);

      // console.log("Profile photo: upload/update complete");
      setUploadingPhoto(false);
    } catch (err) {
      console.error("Profile photo upload error:", err);
      setUploadingPhoto(false);
      presentAlert({
        header: isDesktopMode ? "Upload Failed" : "Photo Upload Failed",
        message: isDesktopMode
          ? "Could not upload profile picture. Please try again."
          : "Unable to update your profile photo right now. Please try again.",
        buttons: ["OK"],
      });
    }
  }, [uploadingPhoto, profile.UserId, currentUser, presentAlert, isDesktopMode]);

  /**
   * Log out user and redirect to login
   * Desktop mode uses window.location.href, mobile uses history.push
   */
  const logoutUser = useCallback(async () => {
    try {
      await signOut(auth);
      
      if (isDesktopMode) {
        // console.log("LOGOUT: Signed out, redirecting to login with returnUrl");
        window.location.href = "/login?returnUrl=/portal";
      } else {
        history.push("./login");
      }
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, [isDesktopMode, history]);

  /**
   * Delete user account with confirmation and error handling
   * Calls Firebase function to mark for deletion, then deletes auth user
   */
  const deleteUserAccount = useCallback(async () => {
    try {
      await fx_FB_DeleteUserAccount(profile.UserId, profile.FirstName);

      await deleteUser(currentUser)
        .then(() => {
          alert(
            "Your account has been deleted. May take up to 24 hours for changes to be reflected."
          );
          
          signOut(auth).then(() => {
            if (isDesktopMode) {
              // console.log("DELETE ACCOUNT: Signed out, redirecting to login with returnUrl");
              window.location.href = "/login?returnUrl=/portal";
            } else {
              history.push("./login");
            }
          });
        })
        .catch((err) => {
          console.error("Delete user error:", err);
          alert(
            "Credentials have expired. Please log back in to delete this account."
          );
          
          signOut(auth).then(() => {
            if (isDesktopMode) {
              // console.log("DELETE ACCOUNT ERROR: Signed out, redirecting to login with returnUrl");
              window.location.href = "/login?returnUrl=/portal";
            } else {
              history.push("./login");
            }
          });
        });
    } catch (err) {
      console.error("Delete account error:", err);
    }
  }, [profile.UserId, profile.FirstName, currentUser, isDesktopMode, history]);

  /**
   * Navigate to admin view
   */
  const navigateToAdminView = useCallback(() => {
    history.push("/AdminView");
  }, [history]);

  /**
   * Navigate to profile edit page
   */
  const navigateToEditProfile = useCallback(() => {
    if (isDesktopMode && onEditProfileRequest) {
      onEditProfileRequest();
      return;
    }

    history.push("/Profile_edit");
  }, [history, isDesktopMode, onEditProfileRequest]);

  /**
   * Navigate to paywall page
   */
  const navigateToPaywall = useCallback((cameFrom: string = "Home.Scheduler") => {
    history.push({
      pathname: "/Paywall",
      state: { cameFrom },
    });
  }, [history]);

  return {
    // Photo management
    uploadProfilePhoto,
    uploadingPhoto,
    
    // Account management
    logoutUser,
    deleteUserAccount,
    
    // Admin
    isAdmin,
    
    // Navigation
    navigateToAdminView,
    navigateToEditProfile,
    navigateToPaywall,
  };
};
